import React from 'react'
import { useAtomValue } from 'jotai'
import styled from 'styled-components'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as state from '../../states'
import {
	type TOfferSpendByAccount,
	type TOfferSpendByAccountAggregated,
	EOfferSpendByAccountView,
	ERoleAccess,
	TOfferSpendByAccountAggOnly,
	TOfferSpendByAccountDay,
	IOfferSpend
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	headCellFormatterSortable,
	ESortOrder,
	IconExternalLink,
	UiTotalLabel
} from '../ui'
import { OFFER_SPEND_BY_ACCOUNT_PAGE_SORT_FIELDS, IOfferSpendByAccountSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferSpendByAccountListTableProps = {
	viewBy: EOfferSpendByAccountView
	range: Array<dt.Dayjs>
	items: TOfferSpendByAccount[];
	skip: number;
	sortBy?: IOfferSpendByAccountSortField;
	setSortBy?: (sortBy: IOfferSpendByAccountSortField) => void;
	order?: ESortOrder;
	setOrder?: (order: ESortOrder) => void;
	onViewOfferSpendDetails?: (item: TOfferSpendByAccountDay | TOfferSpendByAccountAggOnly, e: React.MouseEvent) => void;
}

const RefIf = styled.span`
	margin-left: 8px;
	color: #999;
	font-size: .8rem;
	&::before {
		content: '(';
	}
	&::after {
		content: ')';
	}
`
export const Days = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`
export const Day = styled.div`
	writing-mode: sideways-lr;
	transform-origin: center;
	transform: rotate(45deg);
  /* text-orientation: upright; */
  /* writing-mode: vertical-rl; */
`

const AmountBox =  styled.div`
    min-width: 5ch;
    text-align: center;
`

const Amount = styled.span`
    position: relative;
    transition: all 0.4s;

    & .icon-link {
        position: absolute;
        left: 100%;
        top: 1px;
        opacity: 0;
        transition: all 0.4s;
    }

    &:hover {
        color: var(--mainColor);

        .icon-link {
            color: var(--mainColor);
            opacity: 1;
            transform: translateX(3px);
        }
    }
`
const Total = styled.div`
	min-width: 5ch;
	text-align: center;
`

export const renderTotal = (
	total: Decimal | number,
	rowIndex: number,
	cellIndex: number,
	className?: string
) => {
	const formatValue = helpers.formatAmount(total)

	return <Total className={className ?? ''}>
		{helpers.thousandSeparator(formatValue)}
	</Total>
}

export function renderBy<TFull, TAggOnly> (
	access: boolean | null,
	onViewOfferSpendDetails?: (item: TFull | TAggOnly, e: React.MouseEvent) => void,
) {
	return function <TItemAggr extends Pick<IOfferSpend, 'amount'>, TItem, TItemEnumView>(
		agg: Array<TItemAggr>,
		rowData:  TItem | null,
		rowIndex: number,
		cellIndex: number,
		index: number,
		by: TItemEnumView,
		day: string
	) {
		const id = `${by}-${rowIndex}:${cellIndex}-${index}`
		if (agg.length > 0) {
			const amount: Decimal = agg
				.map(_ => _.amount)
				.reduce((sum, amount) => sum.add(amount), Decimal(0))
			const formatValue = helpers.formatAmount(amount)

			return <>
				{
					!!onViewOfferSpendDetails && !!access
						? (
							<AmountBox>
								<Amount style={{
									cursor: 'pointer'
								}}
								        onClick={(e) => {
									        //console.log('OfferSpendByAccountListTable: ', {rowData})
									        if (!rowData) {
										        onViewOfferSpendDetails && onViewOfferSpendDetails({day, agg} as TAggOnly, e)
									        } else {
										        onViewOfferSpendDetails && onViewOfferSpendDetails({...rowData, day, agg} as TFull, e)
									        }
								        }}
								>
									{helpers.thousandSeparator(formatValue)}
									<i className={'icon-link'}>
										<IconExternalLink fill={'currentColor'}/>
									</i>
								</Amount>
							</AmountBox>
						)
						: null
				}
			</>
		}
		return <AmountBox><Amount>-</Amount></AmountBox>
	}
}

export const OfferSpendByAccountListTable = (props: IOfferSpendByAccountListTableProps) => {
	const {
		viewBy,
		range,
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onViewOfferSpendDetails
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const tableConfig: IUiTableConfig<TOfferSpendByAccount | null>[] = [
		{
			key: 'accountId',
			title: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_account_id),
			rowCellFormatter({ rowData }) {
				if (!rowData) {
					return ''
				}
				return rowData.accountId
			},
		},
		{
			key: 'account.auth.login',
			title: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_account),
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return ''
				}
				const account = rowData?.account ?? null
				const auth = account?.auth ?? null
				const login = auth?.login
				return <>{login ?? '?'}</>
			},
		},
		{
			key: 'account.extra.keitaroLogin',
			title: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_kt_group),
			classesToAdd: {
				th: 'border-end',
				td: 'border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return <UiTotalLabel />
				}

				const extra = rowData?.account?.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.offer_spend_by_account_list_table.column_amount),
			classesToAdd: {
				th: 'text-center',
			},
			rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = rowsData
						.map(_ => _?.amount ?? 0)
						.reduce((sum, amount) => sum.add(amount), Decimal(0))
					return renderTotal(total, rowIndex, cellIndex)
				}
				const total = Decimal(rowData.amount)
				return renderTotal(total, rowIndex, cellIndex)
			},
		},
		...range.map((date: dt.Dayjs, index: number): IUiTableConfig<TOfferSpendByAccount | null> => {
			const day = viewBy === EOfferSpendByAccountView.Month ? date.format('MM.YYYY') : date.format('DD.MM.YYYY')
			return {
				key: `agg-${day}-${index}`,
				title: `${day}`,
				headCellFormatter() {
					return <Days><Day>{day}</Day></Days>
				},
				rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
					const unit = viewBy
					const filterFunc = (item: TOfferSpendByAccountAggregated) => {
						return dt.utc(item.at).startOf(unit).diff(date, unit) === 0
					}

					if (!rowData) {
						const agg = rowsData.map((rowData): TOfferSpendByAccountAggregated[] => {
							const allAgg = rowData?.agg ?? []
							const agg = allAgg.filter(filterFunc)
							return agg
						})

						const aggFlat = agg.flat()
						const total = aggFlat
							.map(_ => _?.amount ?? 0)
							.reduce((sum, amount) => sum.add(amount), Decimal(0))
						return renderTotal(total, rowIndex, cellIndex)
					}
					const allAgg = rowData.agg
					const agg = allAgg.filter(filterFunc)
					return renderBy($access.$(ERoleAccess.OfferSpendByAccountList), onViewOfferSpendDetails)(agg, rowData, rowIndex, cellIndex, index, viewBy, day)
				}
			}
		}),
	]

	const tableConfigSortable: IUiTableConfig<TOfferSpendByAccount>[] = tableConfig.map(
		(configItem) => {
			const configItemIsSortable = OFFER_SPEND_BY_ACCOUNT_PAGE_SORT_FIELDS.includes(configItem.key as IOfferSpendByAccountSortField)

			if (!configItemIsSortable || !sortBy || !setSortBy || !order || !setOrder) { // ?!?!
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('SpendListTable:: ', { pendListTableConfig, roleListTableConfigSortable })
	const { dataItems, tableClassName } = helpers.getTableItemsWithTotalRows(items)

	return <>
		<UiTable
			data={dataItems}
			config={tableConfigSortable}
			// TODO: uncomment if necessary
			classesToAdd={{
				//wrapper: 'table-sticky-wrapper',
				table: tableClassName
			}}
		/>
	</>
}